import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import InactivityGuard from "@/components/InactivityGuard";
import { ThemeProvider } from "@/context/ThemeContext";
import IdleTimeout from "@/components/IdleTimeout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import BusinessesPage from "@/pages/BusinessesPage";
import ProjectSummaryPage from "@/pages/ProjectSummaryPage";
import SharePage from "@/pages/SharePage";
import AdminPage from "@/pages/AdminPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import TimesheetPage from "@/pages/TimesheetPage";
import AtlasPage from "@/pages/AtlasPage";
import LoansPage from "@/pages/LoansPage";
import InsurancePage from "@/pages/InsurancePage";
import SubcontractorPage from "@/pages/SubcontractorPage";
import EinPage from "@/pages/EinPage";
import LicensesPage from "@/pages/LicensesPage";
import SchedulePage from "@/pages/SchedulePage";
import ServicePage from "@/pages/ServicePage";
import ServicePortalPage from "@/pages/ServicePortalPage";
import DailyLogsPage from "@/pages/DailyLogsPage";
import TimesheetFormPage from "@/pages/TimesheetFormPage";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8]">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8]">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  if (role !== "admin" && role !== "secondary_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8]">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <InactivityGuard />
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/businesses" element={<ProtectedRoute><BusinessesPage /></ProtectedRoute>} />
          <Route path="/businesses/:id/project-summary" element={<ProtectedRoute><ProjectSummaryPage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/timesheet" element={<ProtectedRoute><TimesheetPage /></ProtectedRoute>} />
          <Route path="/atlas" element={<SuperAdminRoute><AtlasPage /></SuperAdminRoute>} />
          <Route path="/loans" element={<ProtectedRoute><LoansPage /></ProtectedRoute>} />
          <Route path="/insurance" element={<ProtectedRoute><InsurancePage /></ProtectedRoute>} />
          <Route path="/subcontractors" element={<ProtectedRoute><SubcontractorPage /></ProtectedRoute>} />
          <Route path="/ein" element={<ProtectedRoute><EinPage /></ProtectedRoute>} />
          <Route path="/licenses" element={<ProtectedRoute><LicensesPage /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
          <Route path="/service" element={<ProtectedRoute><ServicePage /></ProtectedRoute>} />
          <Route path="/daily-logs" element={<ProtectedRoute><DailyLogsPage /></ProtectedRoute>} />
          <Route path="/timesheet/form" element={<TimesheetFormPage />} />
          <Route path="/t/:token" element={<TimesheetFormPage />} />
          <Route path="/share/:token" element={<SharePage />} />
          <Route path="/service/portal/:token" element={<ServicePortalPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors duration={3000} />
        <IdleTimeout />
      </AuthProvider>
    </ThemeProvider>
  );
}
